import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Checkbox,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";

const formatAmount = (value, currency) => {
  const n = Number(value);
  const amount = Number.isFinite(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(value ?? "-");
  return currency ? `${amount} ${currency}` : amount;
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : "-");

const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DetailRow = ({ label, value }) => (
  <>
    <Grid item xs={5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
    </Grid>
  </>
);

const AccountView = ({ open, onClose, data, token }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accountTypeLabel =
    data?.account_type === "sarap"
      ? t("account.sarap", "Sarap")
      : t("account.normal", "Normal");
  const [balanceRows, setBalanceRows] = useState([]);
  const [editedBalances, setEditedBalances] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    const nextRows = (data?.currency_balances || []).map((row) => ({
      ...row,
      rowKey: row.id ?? `currency-${row.currency}`,
    }));
    setBalanceRows(nextRows);
    const nextValues = {};
    nextRows.forEach((row) => {
      nextValues[row.rowKey] =
        row.balance != null && row.balance !== ""
          ? String(row.balance)
          : "";
    });
    setEditedBalances(nextValues);
  }, [data?.currency_balances, data?.id]);

  const reconcileMutation = useMutation({
    mutationFn: async ({ row, balance }) => {
      const payload = {
        account: data?.id,
        currency: row.currency,
        balance,
        is_reconciled: true,
      };
      const headers = token ? { Authorization: `Token ${token}` } : {};
      const url = row.id
        ? `${process.env.REACT_APP_API_BASE_URL}/shop/account-currency-balances/${row.id}/`
        : `${process.env.REACT_APP_API_BASE_URL}/shop/account-currency-balances/`;
      const method = row.id ? "patch" : "post";
      const response = await axios[method](url, payload, { headers });
      return response.data;
    },
    onMutate: async ({ row, balance }) => {
      const rowKey = row.rowKey ?? row.id ?? `currency-${row.currency}`;
      const previousRows = balanceRows;
      const previousValues = editedBalances;
      const nextReconciledAt = getLocalDateString();

      setBalanceRows((prev) =>
        prev.map((item) =>
          item.rowKey === rowKey
            ? {
                ...item,
                balance,
                is_reconciled: true,
                reconciled_at: nextReconciledAt,
              }
            : item
        )
      );
      setEditedBalances((prev) => ({
        ...prev,
        [rowKey]: String(balance ?? ""),
      }));

      return { previousRows, previousValues, rowKey, nextReconciledAt };
    },
    onSuccess: (response, variables, context) => {
      const nextRow = {
        ...response,
        rowKey: response.id ?? variables.row.rowKey,
      };
      setEditedBalances((prev) => ({
        ...prev,
        [nextRow.rowKey]: String(nextRow.balance ?? ""),
      }));
      setBalanceRows((prev) =>
        prev.map((row) =>
          row.rowKey === variables.row.rowKey
            ? {
                ...row,
                ...nextRow,
                rowKey: nextRow.rowKey,
              }
            : row
        )
      );
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast.success(t("account.reconciledSaved", "Balance reconciled."));
    },
    onError: (error, variables, context) => {
      if (context?.previousRows) {
        setBalanceRows(context.previousRows);
      }
      if (context?.previousValues) {
        setEditedBalances(context.previousValues);
      }
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.balance?.[0] ||
          error?.response?.data?.is_reconciled?.[0] ||
          error?.message ||
          t("api.failedUpdate", "Failed to update record")
      );
    },
    onSettled: () => {
      setSavingKey(null);
    },
  });

  const handleBalanceChange = (rowKey, value) => {
    setEditedBalances((prev) => ({
      ...prev,
      [rowKey]: value,
    }));
  };

  const handleReconcile = (row) => {
    const rowKey = row.rowKey ?? row.id ?? `currency-${row.currency}`;
    const rawValue = editedBalances[rowKey];
    const balance = row.is_reconciled
      ? row.balance
      : rawValue === "" || rawValue == null
        ? row.balance
        : rawValue;
    if (balance === "" || balance == null || Number.isNaN(Number(balance))) {
      toast.error(t("account.invalidBalance", "Please enter a valid balance."));
      return;
    }

    setSavingKey(rowKey);
    reconcileMutation.mutate({ row, balance });
  };

  const handlePrint = () => {
    if (!data) return;
    const printBalanceRows = balanceRows
      .map(
        (row) => `
          <tr>
            <td class="label">${row.currency_title}</td>
            <td>${formatAmount(row.balance, row.currency_title)}</td>
            <td>${row.is_reconciled ? t("account.reconciled", "Reconciled") : t("account.notReconciled", "Not Reconciled")}</td>
            <td>${formatDate(row.reconciled_at)}</td>
          </tr>`
      )
      .join("");
    const html = `
      <html>
        <head>
          <title>${t("account.accountDetails", "Account details")} #${data?.id || ""}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
            h1, h2 { margin: 0 0 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            td, th { border: 1px solid #e5e7eb; padding: 8px 10px; vertical-align: top; }
            td.label, th { background: #f3f4f6; font-weight: 600; text-align: left; }
            .section { margin-top: 24px; }
          </style>
        </head>
        <body>
          <h1>${data?.name || "-"}</h1>
          <div>${accountTypeLabel}</div>
          <div class="section">
            <h2>${t("account.currencyBalances", "Currency Balances")}</h2>
            <table>
              <thead>
                <tr>
                  <th>${t("account.currency", "Currency")}</th>
                  <th>${t("account.balance", "Balance")}</th>
                  <th>${t("account.reconciled", "Reconciled")}</th>
                  <th>${t("account.reconciledDate", "Reconciled Date")}</th>
                </tr>
              </thead>
              <tbody>${printBalanceRows}</tbody>
            </table>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank", "width=1000,height=800");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("account.accountDetails", "Account details")}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container rowSpacing={1.25}>
            <DetailRow label={t("account.name", "Name")} value={data?.name} />
            <DetailRow label={t("account.type", "Type")} value={accountTypeLabel} />
            <DetailRow label={t("account.note", "Note")} value={data?.note || "-"} />
            <DetailRow label={t("account.id", "ID")} value={data?.id} />
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t("account.currencyBalances", "Currency Balances")}
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small" aria-label="account balance table">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("account.currency", "Currency")}</TableCell>
                    <TableCell>{t("account.balance", "Balance")}</TableCell>
                    <TableCell>{t("account.reconciled", "Reconciled")}</TableCell>
                    <TableCell>{t("account.reconciledDate", "Reconciled Date")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {balanceRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>-</TableCell>
                    </TableRow>
                  ) : (
                    balanceRows.map((row) => (
                      <TableRow key={row.rowKey}>
                        <TableCell>{row.currency_title}</TableCell>
                        <TableCell>
                          {row.is_reconciled ? (
                            formatAmount(row.balance, row.currency_title)
                          ) : (
                            <TextField
                              size="small"
                              value={editedBalances[row.rowKey] ?? row.balance ?? ""}
                              onChange={(e) =>
                                handleBalanceChange(row.rowKey, e.target.value)
                              }
                              inputProps={{ inputMode: "decimal" }}
                              sx={{ minWidth: 160 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={Boolean(row.is_reconciled)}
                            disabled={savingKey === row.rowKey}
                            onClick={(e) => {
                              e.preventDefault();
                              handleReconcile(row);
                            }}
                          />
                          {savingKey === row.rowKey && (
                            <CircularProgress size={18} sx={{ ml: 1, verticalAlign: "middle" }} />
                          )}
                        </TableCell>
                        <TableCell>{formatDate(row.reconciled_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handlePrint} variant="outlined">
          {t("account.print", "Print")}
        </Button>
        <Button onClick={onClose}>{t("account.close", "Close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountView;
